<template>
  <div class="rc-deployment-section">
    <div class="grid-layout">
      <!-- Left Column: Device Selection -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="settings" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Select RC Device</h3>
            <p class="card-subtitle">Choose an RC device to generate a pairing code</p>
          </div>
        </div>

        <!-- RC Device List -->
        <div class="rc-device-list">
          <div
            v-for="device in rcDevices"
            :key="device.deviceID"
            :class="['rc-device-card', { selected: selectedDevice?.deviceID === device.deviceID }]"
            @click="selectedDevice = device"
          >
            <img
              :src="getDeviceImage(device.deviceType)"
              :alt="device.deviceType"
              class="rc-device-img"
            />
            <div class="rc-device-info">
              <p class="rc-device-name">{{ device.deviceName || device.deviceType }}</p>
              <p class="rc-device-id">{{ device.deviceID }}</p>
              <span class="rc-device-type-badge">{{ device.deviceType }}</span>
            </div>
            <div
              v-if="selectedDevice?.deviceID === device.deviceID"
              class="rc-selected-check"
            >
              <Icon name="check" :size="14" color="#1b92a6" />
            </div>
          </div>

          <div v-if="rcDevices.length === 0" class="rc-empty-state">
            <p class="rc-empty-text">No RC devices found in your organization.</p>
          </div>
        </div>
      </div>

      <!-- Right Column: Configuration & Pairing -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="settings" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Pairing Configuration</h3>
            <p class="card-subtitle">
              {{
                selectedDevice
                  ? `Configure pairing for ${selectedDevice.deviceName || selectedDevice.deviceID}`
                  : 'Select a device first'
              }}
            </p>
          </div>
        </div>

        <template v-if="selectedDevice">
          <!-- Device Info Banner -->
          <div class="device-info">
            <p class="device-label">
              Device: <span class="device-value">{{ selectedDevice.deviceName || selectedDevice.deviceID }}</span>
            </p>
            <p class="device-label">
              Type: <span class="device-value">{{ selectedDevice.deviceType }}</span>
            </p>
          </div>

          <!-- Config Mode Selection -->
          <div class="config-options">
            <div class="option-section">
              <label class="option-label">
                <input type="radio" v-model="configMode" value="default" class="radio-input" />
                <span class="radio-custom"></span>
                Use Default Configuration
              </label>
              <p class="option-description">Generate standard configuration with default settings</p>

              <div v-if="configMode === 'default'" class="default-config-options">
                <div class="ip-input-section">
                  <label class="ip-label">
                    Device IP Address (optional)
                    <input
                      v-model="customIpAddress"
                      type="text"
                      placeholder="localhost"
                      class="ip-input"
                    />
                  </label>
                  <div v-if="!isIpValid && customIpAddress.trim()" class="ip-error">
                    Please enter a valid IP address or hostname
                  </div>
                  <p class="ip-description">
                    Leave empty to use localhost. Enter custom IP for remote devices.
                  </p>
                </div>

                <!-- Toggle switches for liveStreamEnable and simulatorEnabled -->
                <div class="toggle-options">
                  <ToggleSwitch
                    label="Live Stream Enable"
                    v-model="liveStreamEnable"
                    :showLabel="true"
                    labelPosition="right"
                  />
                  <ToggleSwitch
                    label="Simulator Enabled"
                    v-model="simulatorEnabled"
                    :showLabel="true"
                    labelPosition="right"
                  />
                </div>
              </div>
            </div>

            <div class="option-section">
              <label class="option-label">
                <input type="radio" v-model="configMode" value="import" class="radio-input" />
                <span class="radio-custom"></span>
                Import Custom Configuration
              </label>
              <p class="option-description">Upload a JSON configuration file with custom settings</p>
            </div>
          </div>

          <!-- Import Section -->
          <div v-if="configMode === 'import'" class="import-section">
            <JsonConfigImport
              :device-type="selectedDevice.deviceType as 'DJI_RC_Plus' | 'DJI_RC_Pro'"
              @config-imported="handleConfigImported"
              @config-cleared="handleConfigCleared"
              @error="handleImportError"
            />
          </div>

          <!-- Error message -->
          <div v-if="importError" class="text-[#e86363] text-xs">
            {{ importError }}
          </div>

          <!-- Actions -->
          <div class="form-actions">
            <button
              class="generate-btn"
              @click="handlePairDevice"
              :disabled="disablePairButton"
            >
              <Loader v-if="isProcessing" />
              <span v-else>Generate Pairing Code</span>
            </button>
            <button class="clear-btn" @click="clearForm" :disabled="isProcessing">
              <span>Clear</span>
            </button>
          </div>
        </template>

        <!-- No device selected state -->
        <div v-else class="rc-no-selection">
          <Icon name="settings" :size="40" color="#606366" />
          <p class="rc-no-selection-text">Select an RC device from the list to configure pairing</p>
        </div>

        <!-- Pairing Results -->
        <div v-if="settings.pairingHistory?.length > 0" class="history-section">
          <div class="history-header">
            <h4 class="history-title">Pairing Codes</h4>
            <button class="clear-history-btn" @click="clearPairingHistory">Clear All</button>
          </div>
          <div class="history-list max-h-[400px] overflow-y-auto">
            <div
              v-for="(result, index) in settings.pairingHistory"
              :key="index"
              class="history-item"
            >
              <div class="history-info">
                <p class="history-machine">{{ result.deviceId }}</p>
                <p class="history-time">{{ result.generatedAt }}</p>
              </div>
              <div>
                <copy :size="18" :value="result.code" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Icon from './Icon.vue'
import JsonConfigImport from './JsonConfigImport.vue'
import ToggleSwitch from '@/components/BaseComponents/ToggleSwitch.vue'
import Loader from '@/components/ui/Loader.vue'
import copy from '@/components/MusterPoint/copy.vue'
import DevicePairing from '@/composables/Device/DevicePairing'
import { rcTypes, getDeviceImage } from '@/constants/deviceManager'
import { appStore } from '@/store/app'
import { uuid } from '@/helpers'
import log from '@/utils/useLogger'
import useUserSettings from '@/composables/useUserSettings'
import type { Device } from '@/types/devices.types'

const { addNotifications } = appStore()
const { pairDevice, organizationDevices } = DevicePairing()
const { settings, saveSettings } = useUserSettings()

// --- Device selection ---
const selectedDevice = ref<Device | null>(null)

const rcDevices = computed(() => {
  return organizationDevices.value.filter((device: Device) =>
    rcTypes.includes(device.deviceType),
  )
})

// --- Pairing config ---
const configMode = ref<'default' | 'import'>('default')
const importedConfig = ref<any>(null)
const importError = ref<string>('')
const isProcessing = ref(false)
const customIpAddress = ref<string>('')
const liveStreamEnable = ref(true)
const simulatorEnabled = ref(true)

const disablePairButton = computed(() => {
  if (!selectedDevice.value) return true
  if (configMode.value === 'import' && !importedConfig.value) return true
  if (isProcessing.value) return true
  if (!isIpValid.value && customIpAddress.value.trim()) return true
  return false
})

const isIpValid = computed(() => {
  const ip = customIpAddress.value.trim()
  if (!ip) return true
  if (['localhost', '127.0.0.1', '::1'].includes(ip)) return true

  const ipv4Regex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  const hostnameRegex =
    /^(?=.{1,253}$)(?!-)[a-zA-Z0-9-]{1,63}(?<!-)(\.(?!-)[a-zA-Z0-9-]{1,63}(?<!-))+$/

  return ipv4Regex.test(ip) || hostnameRegex.test(ip)
})

const handleConfigImported = (config: any) => {
  importedConfig.value = config
  importError.value = ''
}

const handleConfigCleared = () => {
  importedConfig.value = null
  importError.value = ''
}

const handleImportError = (error: string) => {
  importError.value = error
  importedConfig.value = null
}

const handlePairDevice = async () => {
  if (!selectedDevice.value) {
    importError.value = 'No device selected'
    return
  }

  if (configMode.value === 'import' && !importedConfig.value) {
    importError.value = 'Please import a configuration file first'
    return
  }

  if (configMode.value === 'default' && customIpAddress.value.trim() && !isIpValid.value) {
    importError.value = 'Please fix the IP address error before proceeding'
    return
  }

  isProcessing.value = true
  importError.value = ''

  try {
    const device = selectedDevice.value
    const customConfig = configMode.value === 'import' ? importedConfig.value : undefined
    const customIp =
      configMode.value === 'default' ? customIpAddress.value.trim() || undefined : undefined
    const configOptions =
      configMode.value === 'default'
        ? {
            liveStreamEnable: liveStreamEnable.value,
            simulatorEnabled: simulatorEnabled.value,
          }
        : undefined

    const res = await pairDevice(
      device.deviceID,
      device.deviceType as 'DJI_RC_Plus' | 'DJI_RC_Pro',
      customConfig,
      customIp,
      configOptions,
    )

    // Persist to IndexedDB via user settings
    saveSettings({
      pairingHistory: [
        {
          deviceId: device.deviceID,
          code: res?.code || '',
          generatedAt: formatDate(new Date()),
        },
        ...(settings.value.pairingHistory || []),
      ],
    })

    addNotifications({
      id: uuid(10),
      notification: `Pairing code generated for ${device.deviceName || device.deviceID}`,
      type: 'success',
    })
  } catch (error) {
    log.error('Error pairing device:', error)
    importError.value = `Failed to pair device: ${error instanceof Error ? error.message : 'Unknown error'}`
  } finally {
    isProcessing.value = false
  }
}

const clearForm = () => {
  selectedDevice.value = null
  configMode.value = 'default'
  customIpAddress.value = ''
  liveStreamEnable.value = true
  simulatorEnabled.value = true
  importedConfig.value = null
  importError.value = ''
}

const formatDate = (date: Date): string => {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const clearPairingHistory = () => {
  saveSettings({
    pairingHistory: [],
  })
}
</script>

<style scoped>
.rc-deployment-section {
  padding-bottom: 2rem;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 1200px) {
  .grid-layout {
    grid-template-columns: 1fr;
  }
}

.form-card {
  background: linear-gradient(145deg, var(--idi-shade0-100) 0%, rgba(11, 14, 18, 0.95) 100%);
  border: 1px solid var(--idi-shade2-50);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(27, 146, 166, 0.15) 0%, rgba(27, 146, 166, 0.05) 100%);
  border: 1px solid rgba(27, 146, 166, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-title {
  color: #e4e4e4;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.card-subtitle {
  color: #95989c;
  font-size: 13px;
  font-weight: 400;
}

/* Device List */
.rc-device-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
}

.rc-device-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--idi-shade1-50);
  border: 1px solid var(--idi-shade2-50);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.rc-device-card:hover {
  background: var(--idi-shade2-50);
  border-color: var(--idi-shade3-50);
}

.rc-device-card.selected {
  border-color: rgba(27, 146, 166, 0.5);
  background: rgba(27, 146, 166, 0.05);
}

.rc-device-img {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--idi-shade0-50);
  flex-shrink: 0;
}

.rc-device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.rc-device-name {
  color: #e4e4e4;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rc-device-id {
  color: #606366;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rc-device-type-badge {
  display: inline-block;
  width: fit-content;
  color: #95989c;
  font-size: 10px;
  font-weight: 500;
  background: var(--idi-shade0-50);
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 2px;
}

.rc-selected-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(27, 146, 166, 0.15);
  border: 1px solid rgba(27, 146, 166, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rc-empty-state {
  padding: 40px 20px;
  text-align: center;
}

.rc-empty-text {
  color: #606366;
  font-size: 13px;
}

.rc-no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
}

.rc-no-selection-text {
  color: #606366;
  font-size: 13px;
  text-align: center;
}

/* Device Info */
.device-info {
  background-color: var(--idi-shade1-50);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid var(--idi-shade3-50);
}

.device-label {
  color: #95989c;
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 4px;
}

.device-value {
  color: #e4e4e4;
  font-weight: 600;
}

/* Config Options */
.config-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-section {
  border: 1px solid var(--idi-shade3-50);
  border-radius: 8px;
  padding: 12px;
  transition: border-color 0.2s ease;
}

.option-section:has(.radio-input:checked) {
  border-color: #606366;
  background-color: var(--idi-shade1-50);
}

.option-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e4e4e4;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 4px;
}

.radio-input {
  display: none;
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 2px solid var(--idi-shade3-50);
  border-radius: 50%;
  position: relative;
  transition: border-color 0.2s ease;
  flex-shrink: 0;
}

.radio-input:checked + .radio-custom {
  border-color: #606366;
}

.radio-input:checked + .radio-custom::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background-color: #606366;
  border-radius: 50%;
}

.option-description {
  color: #95989c;
  font-size: 12px;
  font-weight: 400;
  margin-left: 24px;
}

.default-config-options {
  margin-top: 16px;
  margin-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ip-input-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ip-label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #e4e4e4;
  font-size: 12px;
  font-weight: 500;
}

.ip-input {
  padding: 8px 12px;
  background-color: var(--idi-shade0-50);
  border: 1px solid var(--idi-shade3-50);
  border-radius: 6px;
  color: #e4e4e4;
  font-size: 12px;
  font-weight: 400;
  transition: border-color 0.2s ease;
}

.ip-input:focus {
  outline: none;
  border-color: #606366;
}

.ip-input::placeholder {
  color: #95989c;
}

.ip-description {
  color: #95989c;
  font-size: 11px;
  font-weight: 400;
  margin-top: 4px;
}

.ip-error {
  color: #e86363;
  font-size: 11px;
  font-weight: 400;
  margin-top: 4px;
}

.toggle-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--idi-shade0-50);
  border-radius: 8px;
  border: 1px solid var(--idi-shade2-50);
}

.import-section {
  border: 1px solid var(--idi-shade3-50);
  border-radius: 8px;
  padding: 16px;
  background-color: var(--idi-shade1-50);
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.generate-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, var(--idi-green) 0%, var(--idi-green-s1) 100%);
  color: #fff;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(27, 146, 166, 0.3);
}

.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--idi-shade3-50);
  color: #95989c;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
}

.clear-btn:hover:not(:disabled) {
  background: var(--idi-shade2-50);
  border-color: var(--idi-shade4-50);
}

.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* History / Pairing Results */
.history-section {
  margin-top: 8px;
  padding-top: 20px;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.history-title {
  color: #95989c;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-history-btn {
  background: transparent;
  border: none;
  color: #606366;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.clear-history-btn:hover {
  color: #fa5353;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: var(--idi-shade1-50);
  border: 1px solid var(--idi-shade2-50);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-item:hover {
  background: var(--idi-shade2-50);
  border-color: var(--idi-shade3-50);
}

.history-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-machine {
  color: #c2c3c4;
  font-size: 13px;
  font-weight: 500;
}

.history-time {
  color: #606366;
  font-size: 11px;
}
</style>
